// Command Center footer showing scenario info, timers, and primary actions
import './Footer.scss'
import useUrlParams from '../../hooks/useUrlParams';
import useUrlNavigation from '../../hooks/useUrlNavigation';
import CircularProgressbar from '../../custom_components/circular_progress/CircularProgressbar';
import { useEffect, useMemo, useState } from 'react';
import useScenario from '../../hooks/useScenario';
import { CircularProgress } from '@mui/material';
import useViewer from '../../hooks/useViewer';
import { get_formated_time } from '../../utils';
import { calculateTimePercentage, get_button_list } from './utils';
import CustomToolTip from '../../../CommonComponents/CustomToolTip';
import useSatellite from '../../hooks/useSatellite';
import ConfirmationDialog from '../../../CommonComponents/confirmation_dialog/ConfirmationDialog';
import useLoader from '../../hooks/useLoader';
import { truncateNumber, showToastMessage } from '../../../../utils/commonFunctions';
import { isOperateMode, isSummaryMode } from '../../hooks/utils';
import { SCENARIO_DEPLOYMENT_TYPES } from '../../../simulate/constants';
import { endSession as endValidateSession } from '../../../validate/api/validate.api';

/** Shared button for Retire / End Simulation with loading state */
const RetireActionButton = ({
  label,
  onClick,
  disabled,
}: {
  label: string
  onClick: () => void
  disabled: boolean
}) => (
  <button disabled={disabled} className="footer__right_button" onClick={onClick}>
    {disabled ? 'Retiring' : label}
    {disabled && <CircularProgress size={16} color="inherit" />}
  </button>
);

const Footer = () => {
  // Load shared loader, selection, scenario, navigation, and viewer state
  const { undeploying } = useLoader()
  const { selected_satellite } = useSatellite()
  const { scenario, simulation_time, undeploy_scenario } = useScenario()
  const { navigate_to, navigate_dashboard } = useUrlNavigation()
  const { dashboard, tab_name, mission_id, scenario_id } = useUrlParams();
  const { current_simulation_time } = useViewer();
  // Local UI state for the end-simulation confirmation dialog and scenario time window
  const [openDialogue, setOpenDialogue] = useState<boolean>(false);
  const [scenarioTime, setScenarioTime] = useState<{
    "start_time": number;
    "end_time": number;
  }>({
    "start_time": 0,
    "end_time": 0,
  })

  // Total simulation duration in minutes (rounded) used for summary display
  const simulation_duration = useMemo(
    () => truncateNumber((scenarioTime?.end_time - scenarioTime?.start_time) / 60, 0),
    [scenarioTime?.start_time, scenarioTime?.end_time]
  )
  // High-level mode flags that drive which footer blocks are visible
  const operateMode = isOperateMode(dashboard, scenario)
  const summaryMode = isSummaryMode(dashboard, scenario)
  const deploymentType = scenario?.scenarioDeploymentType?.toUpperCase();
  const isTrueTwinCommandCenter = deploymentType === SCENARIO_DEPLOYMENT_TYPES.TRUETWIN_COMMAND_CENTER;
  const isValidateScenario = deploymentType === SCENARIO_DEPLOYMENT_TYPES.VALIDATE;
  const show_simulation_btn_container = summaryMode || (dashboard === 'simulation' && !isTrueTwinCommandCenter)
  const isValidateSummary = isValidateScenario && summaryMode
  const show_operate_btn_container = operateMode && !isTrueTwinCommandCenter && !isValidateScenario
  const show_truetwin_btn_container = isTrueTwinCommandCenter
  const show_quick_deploy_btn_container = dashboard === 'quick-deploy'
  // Show timer container for simulation mode, summary mode, and TRUETWIN_COMMAND_CENTER
  const show_scenario__timer__container =
    (summaryMode || dashboard === 'simulation' || isTrueTwinCommandCenter) && current_simulation_time > 0
  const show__summary__timer__container = summaryMode && current_simulation_time > 0

  // Center footer buttons (HUD / Plan / Monitor / Files, etc.)
  const shouldDeferSummaryTabs = dashboard === 'summary' && !scenario?.scenarioDeploymentType
  const ButtonsList = shouldDeferSummaryTabs
    ? []
    : get_button_list(tab_name, dashboard, scenario?.scenarioOperation, scenario)

  // True when simulation has finished or we are in summary view
  const isSimulationCompleteOrSummary = useMemo(
    () =>
      summaryMode ||
      (dashboard === 'simulation' &&
        calculateTimePercentage(scenarioTime?.start_time, scenarioTime?.end_time, current_simulation_time / 1000) === 100),
    [summaryMode, dashboard, scenarioTime?.start_time, scenarioTime?.end_time, current_simulation_time]
  )

  // Keep local scenarioTime in sync with simulation_time from the backend
  useEffect(() => {
    if (simulation_time?.start_time) {
      const start_time = simulation_time?.start_time
      const end_time = simulation_time?.end_time
      setScenarioTime({
        "start_time": start_time,
        "end_time": end_time
      })
    }
  }, [simulation_time]);


  // Handle main footer action click (either navigate away or open confirmation)
  const handleClick = () => {
    if (dashboard === 'quick-deploy' && scenario?.['status'] === 'SimulationCompleted') {
      navigate_dashboard()
    } else if (isSimulationCompleteOrSummary) {
      navigate_dashboard()
    } else {
      setOpenDialogue(true)
    }
  }

  // Compute elapsed simulation minutes for the summary timer
  const durationMins = useMemo(
    () => truncateNumber((current_simulation_time - scenarioTime.start_time * 1000) / 60000, 0),
    [current_simulation_time, scenarioTime.start_time],
  )

  // Validate summary duration from deployment-details simulationDuration (seconds -> minutes).
  const validateSummaryDurationMins = useMemo(() => {
    if (!isValidateSummary) return null

    const satelliteDurationSec = Number(selected_satellite?.value?.simulationDuration)
    if (Number.isFinite(satelliteDurationSec) && satelliteDurationSec >= 0) {
      return truncateNumber(satelliteDurationSec / 60, 0)
    }

    const scenarioDurationSec = Number(scenario?.simulationDuration)
    if (Number.isFinite(scenarioDurationSec) && scenarioDurationSec >= 0) {
      return truncateNumber(scenarioDurationSec / 60, 0)
    }

    const scenarioDurationMins = Number(scenario?.scenarioDuration)
    if (Number.isFinite(scenarioDurationMins) && scenarioDurationMins >= 0) {
      return truncateNumber(scenarioDurationMins, 0)
    }

    const simTimeDurationMins = Number(simulation_time?.duration)
    if (Number.isFinite(simTimeDurationMins) && simTimeDurationMins >= 0) {
      return truncateNumber(simTimeDurationMins, 0)
    }

    return 0
  }, [
    isValidateSummary,
    selected_satellite?.value?.simulationDuration,
    scenario?.simulationDuration,
    scenario?.scenarioDuration,
    simulation_time?.duration,
  ])

  // VALIDATE running must use the stable session start from backend details.
  // simulation_time.start_time can shift with the viewer timeframe window, which
  // makes the footer diverge from the session-list elapsed timer.
  const validateSessionStartSec = useMemo(() => {
    const satelliteStartSec = Number(selected_satellite?.value?.simulationStartTime)
    if (Number.isFinite(satelliteStartSec) && satelliteStartSec > 0) return satelliteStartSec

    const scenarioStartSec = Number(scenario?.simulationStartTime)
    if (Number.isFinite(scenarioStartSec) && scenarioStartSec > 0) return scenarioStartSec

    const frameStartSec = Number(scenarioTime?.start_time)
    if (Number.isFinite(frameStartSec) && frameStartSec > 0) return frameStartSec

    return null
  }, [
    selected_satellite?.value?.simulationStartTime,
    scenario?.simulationStartTime,
    scenarioTime?.start_time,
  ])

  // VALIDATE running: elapsed = time (mm:ss) since session start.
  const validateElapsedLabel = useMemo(() => {
    if (!isValidateScenario || summaryMode) return null
    const startSec = validateSessionStartSec
    if (startSec == null || startSec <= 0) return null
    const nowSec = current_simulation_time / 1000
    const elapsedSec = Math.max(0, nowSec - startSec)
    const hh = Math.floor(elapsedSec / 3600)
    const mm = Math.floor((elapsedSec % 3600) / 60)
    const ss = Math.floor(elapsedSec % 60)

    // < 60 minutes: show "XmYs"
    if (elapsedSec < 3600) {
      const totalMins = Math.floor(elapsedSec / 60)
      return `${totalMins}m${String(ss).padStart(2, '0')}s`
    }

    // >= 60 minutes: show "Xh Ym Zs" (more readable than hh:mm:ss)
    return `${hh}h ${mm}m ${ss}s`
  }, [isValidateScenario, summaryMode, validateSessionStartSec, current_simulation_time])

  // VALIDATE: end running session (no timer/progress; just UTC + End Session)
  const handleEndValidateSession = async () => {
    if (!mission_id || !scenario_id) return;
    try {
      await endValidateSession(mission_id, scenario_id);
      navigate_dashboard();
    } catch (err) {
      showToastMessage('error', 'Failed to end session');
    }
  };

  return (
    <div className={`command__center_footer${!selected_satellite ? ' disabled' : ''}`}>
      {/* Left section: scenario name and current mode label */}
      <div className='footer__left_button_container'>
        <div className='footer__left__button'>
          <CustomToolTip title={scenario?.scenarioName ?? ''} placement="top-start">
            <span className='z-99'>{scenario?.scenarioName}</span>
          </CustomToolTip>
          <div className='footer__left__button flex-row'>
            <span className='deployment-type'> {isValidateScenario && summaryMode ? 'Validate' : scenario?.scenarioOperation} :</span>
            <span>{summaryMode ? 'Summary' : dashboard === 'quick-deploy' ? 'Quick Deploy' : dashboard === 'simulation' ? 'Running Simulation' : isValidateScenario ? 'Validate' : isTrueTwinCommandCenter ? 'TrueTwin Command Center' : 'Operate'}</span>
          </div>
        </div>
      </div>
      {/* Center section: navigation buttons for HUD / Plan / Monitor / Files */}
      <div className='footer__center_button_container'>
        {ButtonsList.map((button) => {
          const { label, svg, show, active } = button;
          return (
            <button
              key={label}
              disabled={scenario?.scenarioOperation === 'LEOP' && label === 'Plan'}
              className={`footer__center__button ${active && 'active'} ${!show && 'hide'}`}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation()
                const tabname = label.toLowerCase() === 'plan' ? 'plan-onboard-schedule' : label.toLowerCase() === 'files' ? 'files-ground-files' : label.toLowerCase();
                navigate_to(tabname)
              }}
            >
              {svg}
              <span>{label}</span>
            </button>
          )
        })}
      </div>
      {/* Middle section: simulation progress ring and timers (not shown for running VALIDATE sessions) */}
      {show_scenario__timer__container &&
        <div className='scenario__timer__container'>
          {current_simulation_time > 0 && (
            <>
              <CircularProgressbar
                simulation_current_time={current_simulation_time / 1000}
                simulation_start_time={scenarioTime?.start_time}
                simulation_end_time={scenarioTime?.end_time}
              />
              <div className='separator' />
            </>
          )}
          {show__summary__timer__container && !isValidateSummary &&
            <div className='time__container'>
              <div className='d-flex gap-4 align-items-center'>
                <span>{isValidateScenario && summaryMode ? validateSummaryDurationMins : durationMins} mins</span>
                {!isValidateScenario && durationMins !== simulation_duration && <span className='opacity-40'>{simulation_duration} mins</span>}
              </div>
              <span className='opacity-60'>{isValidateScenario ? 'Session Duration' : 'Simulation Duration'}</span>
            </div>
          }
          {!show__summary__timer__container &&
            <div className='time__container'>
              <span>{current_simulation_time && get_formated_time(current_simulation_time / 1000)}</span>
              <span>{scenarioTime?.end_time && get_formated_time(scenarioTime?.end_time)}</span>
            </div>
          }
        </div>
      }
      {/* Right section: End/Close action for simulation and summary (or Validate summary = Close) */}
      {show_simulation_btn_container && (summaryMode || !isValidateScenario) && (
        <div className="footer__right_button_container">
          <div className="d-flex align-items-center gap-3">
            {isValidateSummary && (
              <div className="d-flex flex-column text-start">
                <span>{(validateSummaryDurationMins ?? 0)} mins</span>
                <span className="opacity-60">{isValidateScenario ? 'Session Duration' : 'Simulation Duration'}</span>
              </div>
            )}
            <button
              disabled={undeploying}
              className="footer__right_button"
              onClick={handleClick}
            >
              {(summaryMode ||
                calculateTimePercentage(
                  scenarioTime?.start_time,
                  scenarioTime?.end_time,
                  current_simulation_time / 1000
                ) === 100)
                ? 'Close'
                : undeploying
                  ? 'Retiring'
                  : 'End Simulation'}
              {undeploying && <CircularProgress size={16} color="inherit" />}
            </button>
          </div>
        </div>
      )}
      {/* Right section: Retire action for quick-deploy scenarios (hidden once completed) */}
      {show_quick_deploy_btn_container && scenario?.status !== 'SimulationCompleted' && (
        <div className="footer__right_button_container">
          {scenario && (
            <RetireActionButton label="Retire" onClick={handleClick} disabled={undeploying} />
          )}
        </div>
      )}
      {/* Right section: Back navigation when operating in non-TrueTwin, non-VALIDATE mode */}
      {show_operate_btn_container && (
        <div className="footer__right_button_container">
          <button className="footer__right_button" onClick={navigate_dashboard}>
            Back
          </button>
        </div>
      )}
      {/* Right section: VALIDATE only — Elapsed Time + End Session (running); summary uses block above with Close) */}
      {isValidateScenario && !summaryMode && (
        <div className="footer__right_button_container">
          <div className="d-flex align-items-center gap-3">
            <div className="d-flex flex-column text-start">
              <span>{validateElapsedLabel ?? '—'}</span>
              <span className="opacity-60">Elapsed Time</span>
            </div>
            <button
              disabled={undeploying}
              className="footer__right_button"
              onClick={handleEndValidateSession}
            >
              {undeploying ? 'Ending Session…' : 'End Session'}
              {undeploying && <CircularProgress size={16} color="inherit" />}
            </button>
          </div>
        </div>
      )}
      {/* Right section: End Simulation action for TrueTwin Command Center */}
      {show_truetwin_btn_container && !summaryMode && !show_simulation_btn_container && (
        <div className="footer__right_button_container">
          <RetireActionButton
            label="End Simulation"
            onClick={() => setOpenDialogue(true)}
            disabled={undeploying}
          />
        </div>
      )}

      {/* Confirmation dialog for ending or retiring the current simulation */}
      <ConfirmationDialog
        type='cancel'
        open={openDialogue}
        title={`${isTrueTwinCommandCenter ? "End TrueTwin Command Center Simulation" : dashboard === 'quick-deploy' ? "Retire Quick Deploy Simulation" : 'End Scenario Simulation'}?`}
        description={`${isTrueTwinCommandCenter ? "Ending the simulation will stop this TrueTwin Command Center simulation and summary will be available only for the simulation's runtime." : dashboard === 'quick-deploy' ? "Ending the simulation will stop this Quick Deploy simulation and summary will be available only for the simulation's runtime." : "Ending the simulation will stop the scenario and summary will be available only for the simulation's runtime."}`}
        onCancel={() => setOpenDialogue(false)}
        onConfirm={() => {
          // TRUETWIN_COMMAND_CENTER uses same undeploy API as simulation mode
          // API: SimulateService.unDeployScenario(mission_id, scenario_id)
          // Endpoint: POST /test/${missionId}/scenario/${scenarioId}/undeployment
          undeploy_scenario()
          setOpenDialogue(false)
        }}
        confirm_button_text={`${isTrueTwinCommandCenter ? 'End Simulation' : dashboard === 'quick-deploy' ? 'Retire' : "End Simulation"}`}
      />
    </div>
  )
}

export default Footer
